/**
 * Revalidação automática de tela: o que muda por ação de outra pessoa.
 *
 * O duelo é assíncrono de propósito — cada um joga quando quiser, e é isso que dispensa
 * WebSocket (ROADMAP, Frente 5). O preço dessa escolha é que nada avisa o cliente quando
 * o adversário fecha a chain dele: a tela só conta a verdade do instante em que foi
 * aberta, e quem deixa `/duelos` aberto nunca vê o resultado chegar.
 *
 * Isto é o remendo desse preço, e vive num lugar só porque os três cuidados abaixo são
 * fáceis de esquecer quando cada tela escreve o próprio `setInterval`:
 *
 * - **Aba oculta não consulta.** Uma aba de fundo esquecida bateria no servidor o dia
 *   inteiro sem ninguém olhando.
 * - **Voltar para a aba revalida na hora.** É exatamente quando o dado velho incomoda;
 *   esperar o próximo tique mostraria o placar de antes para quem acabou de voltar.
 * - **Carga em voo não é atropelada.** Em rede lenta os pedidos se acumulariam, e uma
 *   resposta antiga chegando depois da nova sobrescreveria a tela com o passado.
 *
 * A revalidação é melhoria, não caminho principal: ela nunca acende estado de carregando
 * e nunca propaga erro. Quem chama passa uma carga silenciosa — ver `load(silent)` nas
 * views que a usam.
 */

/** Piso do intervalo. Abaixo disso não é revalidar, é bater no servidor. */
const MINIMUM_SECONDS = 5;

/**
 * Janela em que uma segunda revalidação é ignorada.
 *
 * Voltar para a aba dispara `visibilitychange` e `focus` quase juntos, e sem isto o
 * retorno custaria duas requisições em vez de uma.
 */
const DEBOUNCE_MS = 1000;

/**
 * Passa a revalidar `load` enquanto a tela estiver visível.
 *
 * @param {() => Promise<unknown>} load carga silenciosa da tela
 * @param {{ seconds?: number }} options intervalo entre revalidações
 * @returns {{ now: () => Promise<void>, stop: () => void }} `stop` é obrigatório no
 *   `beforeUnmount`: sem ele o intervalo sobrevive à troca de rota.
 */
export function startLiveRefresh(load, { seconds = 20 } = {}) {
  const every = Math.max(MINIMUM_SECONDS, seconds) * 1000;
  let running = false;
  let stopped = false;
  let lastRunAt = 0;

  async function run() {
    const now = Date.now();
    if (stopped || running || document.hidden || now - lastRunAt < DEBOUNCE_MS) return;
    running = true;
    lastRunAt = now;
    try {
      await load();
    } catch {
      // Falhar aqui deixa a tela com o dado anterior, que é melhor do que um erro que
      // ninguém pediu: o jogador não clicou em atualizar.
    } finally {
      running = false;
      lastRunAt = Date.now();
    }
  }

  const timer = setInterval(run, every);

  // Sair da aba não precisa de tratamento: o tique já recusa sozinho quando `hidden`.
  const onVisible = () => {
    if (!document.hidden) run();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);

  return {
    now: run,
    stop() {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    },
  };
}
